/* global ModalDialog */

/**
 * <fembites-quick-add>
 *
 * Quick Add overlay shown from a Fembites PLP card. Mirrors the PDP
 * variant + subscription selector (designbites-variant-abo-selector v2 +
 * designbites-buy-buttons v2), but with all behaviour scoped to the modal
 * instance so 16 cards on a PLP don't interfere with each other.
 *
 * Extends ModalDialog (assets/global.js) for focus trap, escape-to-close,
 * and body-scroll-lock. The inner <product-form> handles ATC; the modal
 * close + cart-drawer refresh on success is wired by product-form.js
 * (already extended to recognise <fembites-quick-add>).
 *
 * Click handling uses EVENT DELEGATION on the modal root rather than
 * per-element listeners. This is robust against:
 *   - the element being moved to document.body by ModalDialog
 *   - children being added/removed dynamically
 *   - timing issues if the DOM is touched between the constructor and
 *     connectedCallback
 */

if (!customElements.get("fembites-quick-add")) {
  class FembitesQuickAdd extends ModalDialog {
    constructor() {
      super();
      this._wired = false;
    }

    connectedCallback() {
      // ModalDialog.connectedCallback() moves us to <body>. We wire up after
      // that so all queries are stable regardless of where we end up.
      super.connectedCallback();
      if (this._wired) return;
      this._wired = true;

      this.dataEl = this.querySelector("[data-fqa-data]");
      this.data = this.dataEl ? safeJSON(this.dataEl.textContent) : null;

      this.form = this.querySelector("form[data-type='add-to-cart-form']");
      this.variantInput = this.form?.querySelector("input[name='id']");
      this.quantityInput = this.form?.querySelector("input[name='quantity']");
      this.sellingPlanInput = this.form?.querySelector(
        "input[name='selling_plan']"
      );
      this.ctaLabel = this.querySelector(".fembites-quick-add__cta-label");
      this.ctaPrice = this.querySelector(".price-item--actual-price");
      this.ctaCompare = this.querySelector(".price-item.label--Strike");

      this.bindDelegatedClicks();
      this.bindRadioChanges();
      this.bindQuantityStepper();
      this.bindBackdrop();
      this.applyInitialState();
    }

    /**
     * Override show() to inject a body-level backdrop element with inline
     * styles. We do this in JS rather than relying on the CSS-driven
     * backdrop child div because some browser/theme combinations were
     * silently failing to render the backdrop (the user-reported "no
     * background" bug). Inline styles on a body-level element are immune
     * to stacking-context issues, ad-blockers, theme CSS overrides, and
     * any caching problems with the asset CSS file.
     */
    show(opener) {
      console.log("[fembites-quick-add] show() called", this.id);
      if (!this._injectedBackdrop) {
        const bd = document.createElement("div");
        bd.className = "fembites-quick-add__injected-backdrop";
        // Inline styles win every CSS fight: theme overrides, ad-blockers
        // injecting their own !important rules, etc. The !important markers
        // are belt-and-braces.
        bd.style.cssText = [
          "position:fixed !important",
          "top:0 !important",
          "left:0 !important",
          "right:0 !important",
          "bottom:0 !important",
          "width:100vw !important",
          "height:100vh !important",
          "background-color:rgba(79, 39, 111, 0.7) !important",
          "backdrop-filter:blur(6px)",
          "-webkit-backdrop-filter:blur(6px)",
          "z-index:9998 !important",
          "cursor:pointer",
          "display:block !important",
          "opacity:1 !important",
          "visibility:visible !important",
          "pointer-events:auto !important",
        ].join(";");
        bd.addEventListener("click", () => this.hide());
        document.body.appendChild(bd);
        this._injectedBackdrop = bd;
        console.log(
          "[fembites-quick-add] backdrop injected at body level",
          bd
        );
      }
      super.show(opener);
    }

    hide() {
      super.hide();
      if (this._injectedBackdrop) {
        this._injectedBackdrop.remove();
        this._injectedBackdrop = null;
      }
    }

    /* ------------------------------------------------------------------
     * Event delegation: a single click handler on the modal root catches
     * everything that bubbles up. This is more robust than per-element
     * listeners attached at construction time.
     * ------------------------------------------------------------------ */
    bindDelegatedClicks() {
      this.addEventListener("click", (e) => {
        // Subscription chip click — must check this BEFORE the
        // purchase-type-option check because chips are descendants of the
        // option.
        const tag = e.target.closest(".subscription-tag");
        if (tag && this.contains(tag)) {
          if (tag.dataset.disabled === "true") return;
          this.selectSubscriptionTag(tag);
          return;
        }

        // Quantity buttons handled by bindQuantityStepper directly to allow
        // stop-propagation, but defend against stray clicks here.
        if (e.target.closest(".quantity-btn")) return;

        // Purchase-type click: anywhere inside an option that ISN'T inside
        // the inner content area.
        const option = e.target.closest(".purchase-type-option");
        if (option && this.contains(option)) {
          if (
            e.target.closest(".subscription-content") ||
            e.target.closest(".onetime-content")
          ) {
            return;
          }
          this.applyPurchaseType(option.dataset.purchaseType);
        }
      });
    }

    /* ------------------------------------------------------------------
     * Radio change handler: redundant safety net in case a click somehow
     * doesn't bubble (e.g. label-for-radio interaction in some browsers).
     * ------------------------------------------------------------------ */
    bindRadioChanges() {
      this.querySelectorAll(".purchase-type-radio").forEach((radio) => {
        radio.addEventListener("change", () => {
          if (!radio.checked) return;
          const option = radio.closest(".purchase-type-option");
          if (option) this.applyPurchaseType(option.dataset.purchaseType);
        });
      });
    }

    /* ------------------------------------------------------------------ */
    bindBackdrop() {
      const backdrop = this.querySelector(".fembites-quick-add__backdrop");
      if (backdrop) {
        backdrop.addEventListener("click", () => this.hide());
      }
    }

    /* ------------------------------------------------------------------
     * Quantity stepper — bound directly because the buttons need
     * stopPropagation to prevent the parent option from re-toggling.
     * ------------------------------------------------------------------ */
    bindQuantityStepper() {
      const wrapper = this.querySelector(".quantity-selector-wrapper");
      if (!wrapper) return;

      const minus = wrapper.querySelector(".quantity-btn--minus");
      const plus = wrapper.querySelector(".quantity-btn--plus");
      const display = wrapper.querySelector(".quantity-display");
      if (!minus || !plus || !display) return;

      const minQty = 1;
      const maxQty = 10;
      this.quantity = 1;

      // Stop clicks from bubbling to the parent purchase-type-option.
      wrapper.addEventListener("click", (e) => e.stopPropagation());

      const setQty = (newQty) => {
        const q = Math.max(minQty, Math.min(maxQty, newQty));
        this.quantity = q;
        display.textContent = q;
        minus.disabled = q <= minQty;
        plus.disabled = q >= maxQty;
        if (this.quantityInput) this.quantityInput.value = q;
        if (this.purchaseType === "onetime") {
          this.updateCtaPriceFromVariant(wrapper.dataset.variantId, q);
        }
      };

      minus.addEventListener("click", (e) => {
        e.stopPropagation();
        setQty(this.quantity - 1);
      });
      plus.addEventListener("click", (e) => {
        e.stopPropagation();
        setQty(this.quantity + 1);
      });

      this._setQty = setQty;
    }

    /* ==================================================================
     * State transitions
     * ================================================================== */

    applyInitialState() {
      const active = this.querySelector(".purchase-type-option--active");
      const initialType = active ? active.dataset.purchaseType : "onetime";
      this.applyPurchaseType(initialType, /* skipDefaultPick */ false);
    }

    applyPurchaseType(type, skipDefaultPick) {
      this.purchaseType = type;

      // Toggle active class + radio checked state on every option.
      this.querySelectorAll(".purchase-type-option").forEach((opt) => {
        const isActive = opt.dataset.purchaseType === type;
        opt.classList.toggle("purchase-type-option--active", isActive);
        const radio = opt.querySelector(".purchase-type-radio");
        if (radio) radio.checked = isActive;
      });

      if (type === "subscription") {
        // Pick a subscription chip if one isn't already selected.
        const tags = Array.from(this.querySelectorAll(".subscription-tag"));
        let selected = this.querySelector(
          ".subscription-tag--selected:not([data-disabled='true'])"
        );
        if (!selected) {
          selected = tags.find((t) => t.dataset.disabled !== "true");
        }
        if (selected) {
          this.selectSubscriptionTag(selected, /* silent */ true);
        }
        this.updateCtaLabel(true);
      } else {
        // One-time path: clear selling plan, reset to first variant + qty 1.
        this.setSellingPlan("");
        const wrapper = this.querySelector(".quantity-selector-wrapper");
        const firstVariantId = wrapper?.dataset.variantId;
        if (firstVariantId) this.setVariant(firstVariantId);
        if (this._setQty) this._setQty(1);
        else if (this.quantityInput) this.quantityInput.value = 1;
        if (firstVariantId) {
          this.updateCtaPriceFromVariant(firstVariantId, 1);
        }
        this.updateCtaLabel(false);
      }
    }

    selectSubscriptionTag(tag, silent) {
      this.querySelectorAll(".subscription-tag").forEach((t) =>
        t.classList.remove("subscription-tag--selected")
      );
      tag.classList.add("subscription-tag--selected");

      const variantId = tag.dataset.mappedVariantId;
      const planId = tag.dataset.sellingPlanId;
      const subPrice = parseInt(tag.dataset.allocationPrice, 10);
      // Mirrors PDP `designbites-buy-buttons-v2`: strikethrough source
      // is `allocation.compare_at_price` (carried on the chip as
      // `data-allocation-compare-price`), not the variant's one-time
      // price. Hidden by updateCtaPrice when not > price.
      const comparePrice = parseInt(tag.dataset.allocationComparePrice, 10);

      if (variantId) this.setVariant(variantId);
      this.setSellingPlan(planId);
      // Quantity is always 1 — the variant itself already represents
      // N packs (e.g. "6 Packungen (Kapseln)" is a single line item).
      if (this.quantityInput) this.quantityInput.value = 1;

      // Update bottom CTA only. The Spar-Abo card header shows static
      // "ab" lowest values that don't change on chip click — same
      // behaviour as the PDP `designbites-variant-abo-selector-v2`.
      if (subPrice) this.updateCtaPrice(subPrice, comparePrice);

      if (!silent) this.updateCtaLabel(true);
    }

    /* ==================================================================
     * Form mutations
     * ================================================================== */

    setVariant(variantId) {
      if (this.variantInput && variantId)
        this.variantInput.value = variantId;
    }

    setSellingPlan(planId) {
      if (this.sellingPlanInput) this.sellingPlanInput.value = planId || "";
    }

    /* ==================================================================
     * CTA updates
     * ================================================================== */

    updateCtaLabel(isSubscription) {
      if (!this.ctaLabel || !this.data?.labels) return;
      this.ctaLabel.textContent = isSubscription
        ? this.data.labels.subscribeNow
        : this.data.labels.orderNow;
    }

    updateCtaPrice(priceCents, comparePriceCents) {
      if (this.ctaPrice) this.ctaPrice.textContent = formatMoney(priceCents, this.currency());
      if (this.ctaCompare) {
        if (comparePriceCents && comparePriceCents > priceCents) {
          this.ctaCompare.textContent = formatMoney(comparePriceCents, this.currency());
          this.ctaCompare.style.display = "";
        } else {
          this.ctaCompare.style.display = "none";
        }
      }
    }

    updateCtaPriceFromVariant(variantId, qty) {
      if (!this.data || !variantId) return;
      const v = this.data.variants?.[variantId];
      if (!v) return;
      const total = v.price * (qty || 1);
      const totalCompare = v.compareAt ? v.compareAt * (qty || 1) : 0;
      this.updateCtaPrice(total, totalCompare);
    }

    currency() {
      return this.data?.currencySymbol || "€";
    }
  }

  function safeJSON(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      console.error("[fembites-quick-add] failed to parse data block", e);
      return null;
    }
  }

  function formatMoney(cents, symbol) {
    return `${formatMoneyWithoutCurrency(cents)} ${symbol || "€"}`;
  }

  function formatMoneyWithoutCurrency(cents) {
    return (cents / 100).toFixed(2).replace(".", ",");
  }

  customElements.define("fembites-quick-add", FembitesQuickAdd);
}
